/**
 * WebRTC Utilities for WhatsApp Business Calling (VoIP).
 *
 * Implements a signaling adapter designed to resolve STUN/TURN configurations,
 * track candidate gathering, handle non-trickle ICE (v1), apply remote SDP answers,
 * and mount incoming streams to dynamic DOM `<audio>` components.
 */

// TODO (Future Phase): Uncomment this import to restore configuration
// import { TURN_CREDENTIAL, TURN_URL, TURN_USERNAME } from 'src/config';

export function getWhatsAppIceServers(): RTCIceServer[] {
  // TODO (Future Phase): Uncomment this to enable the default Google STUN server
  // const servers: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
  const servers: RTCIceServer[] = [];

  // TODO (Future Phase): Uncomment this to enable TURN configuration
  // TURN is disabled in this phase
  /*
  if (TURN_URL) {
    servers.push({
      urls: TURN_URL,
      ...(TURN_USERNAME ? { username: TURN_USERNAME } : {}),
      ...(TURN_CREDENTIAL ? { credential: TURN_CREDENTIAL } : {}),
    });
  }
  */

  return servers;
}

/**
 * Instantiates a standard RTCPeerConnection pre-populated with ICE configuration and pool sizes.
 */
export function createWhatsAppPeerConnection(): RTCPeerConnection {
  return new RTCPeerConnection({
    iceServers: getWhatsAppIceServers(),
    iceCandidatePoolSize: 10,
  });
}

export type LocalDescriptionOptions = {
  /** Max wait before sending SDP with gathered candidates so far (default 15000). */
  maxWaitMs?: number;
  /** Minimum wait before early resolve when usable candidates exist (default 0). */
  minWaitMs?: number;
};

function countIceCandidatesInSdp(sdp: string): number {
  return (sdp.match(/a=candidate:/g) ?? []).length;
}

function sdpHasUsableCandidate(sdp: string): boolean {
  return /typ (host|srflx|relay)/.test(sdp);
}

/**
 * Helper implementing non-trickle ICE candidate gathering (v1 calling contract).
 * Since the backend does not relay individual ICE candidates dynamically, the complete
 * list of candidates must be bundled within the initial SDP payload.
 *
 * This function creates a promise that:
 * 1. Resolves immediately if the gathering state is already 'complete'.
 * 2. Listens for 'icegatheringstatechange' and resolves once gathering is complete.
 * 3. Falls back to a maximum timeout (default 15s) to avoid blocking indefinitely.
 * 4. Resolves early (after `minWaitMs`) if at least one usable candidate (host, srflx, or relay) is gathered.
 *
 * @param pc The RTCPeerConnection instance to observe
 * @param options Timeout boundaries (`maxWaitMs` and `minWaitMs`)
 */
export function localDescriptionWhenReady(
  pc: RTCPeerConnection,
  options: LocalDescriptionOptions = {}
): Promise<string> {
  const maxWaitMs = options.maxWaitMs ?? 15000;
  const minWaitMs = options.minWaitMs ?? 0;

  // Immediate resolve if gathering is already finished
  if (pc.iceGatheringState === 'complete' && pc.localDescription?.sdp) {
    return Promise.resolve(pc.localDescription.sdp);
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let settled = false;

    const cleanup = () => {
      window.clearTimeout(maxTimer);
      pc.removeEventListener('icegatheringstatechange', onGatheringChange);
      pc.removeEventListener('icecandidate', onIceCandidate);
    };

    const finish = (sdp: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(sdp);
    };

    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };

    const tryFinish = () => {
      const sdp = pc.localDescription?.sdp;
      if (!sdp) return;

      if (pc.iceGatheringState === 'complete') {
        finish(sdp);
        return;
      }

      const elapsed = Date.now() - startedAt;
      if (elapsed >= maxWaitMs) {
        finish(sdp);
        return;
      }

      if (
        elapsed >= minWaitMs &&
        countIceCandidatesInSdp(sdp) >= 1 &&
        sdpHasUsableCandidate(sdp)
      ) {
        finish(sdp);
      }
    };

    const onGatheringChange = () => tryFinish();

    const onIceCandidate = (event: RTCPeerConnectionIceEvent) => {
      if (!event.candidate) {
        tryFinish();
        return;
      }
      tryFinish();
    };

    const maxTimer = window.setTimeout(() => {
      const sdp = pc.localDescription?.sdp;
      if (sdp) {
        finish(sdp);
        return;
      }
      fail('ICE gathering timed out');
    }, maxWaitMs);

    pc.addEventListener('icegatheringstatechange', onGatheringChange);
    pc.addEventListener('icecandidate', onIceCandidate);
    tryFinish();
  });
}

/** Apply outbound SDP answer; idempotent when the answer was already set. */
export async function applyRemoteSdpAnswer(
  pc: RTCPeerConnection,
  sdpAnswer: string
): Promise<'applied' | 'already_applied' | 'wrong_state'> {
  if (pc.signalingState === 'stable') {
    if (pc.remoteDescription?.type === 'answer') {
      return 'already_applied';
    }
    return 'wrong_state';
  }

  if (pc.signalingState !== 'have-local-offer') {
    return 'wrong_state';
  }

  await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
  return 'applied';
}

export function attachRemoteAudio(pc: RTCPeerConnection, callId: string): void {
  pc.ontrack = (event) => {
    // Meta's SDP answer may omit msid, leaving event.streams empty; wrap the track ourselves.
    const stream = event.streams[0] ?? new MediaStream([event.track]);

    let el = document.getElementById(`wa-audio-${callId}`) as HTMLAudioElement | null;
    if (!el) {
      // console.log(`[WA Call] Creating DOM audio element for call ${callId}`);
      el = document.createElement('audio');
      el.id = `wa-audio-${callId}`;
      el.autoplay = true;
      document.body.appendChild(el);
    }
    el.srcObject = stream;
    el.play().catch((error) => {
      console.warn(`[WA Call] Remote audio playback blocked for call ${callId}:`, error);
    });
    // console.log(`[WA Call] Remote audio stream attached to element #wa-audio-${callId}`);
  };
}

export function teardownPeer(
  callId: string,
  peers: Map<string, { pc: RTCPeerConnection; stream: MediaStream }>
) {
  const entry = peers.get(callId);
  if (!entry) return;

  entry.stream.getTracks().forEach((track) => track.stop());
  entry.pc.getSenders().forEach((sender) => sender.track?.stop());
  entry.pc.close();
  peers.delete(callId);

  document.getElementById(`wa-audio-${callId}`)?.remove();
}
